import React, { useEffect, useState, useRef, useCallback } from "react";
import InnerTitle from "../../components/InnerTitle";
import Card from "../../components/Card";
import { GetStreams, subscribeReminder, favouriteUnfavouriteStream, getStaffAssignedStreams } from "../../services/stream.service";
import { getMyInvitations } from "../../services/user.service";
import { 
  GetStreamsDetails as GetAdvertiserStreams, 
  deleteStream, 
  startStream, 
  createAndForwardStream 
} from "../../../advertiser/services/stream.service";
import { ROLE, SOCKET_EVENTS, SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateAndTime, getUser, handleServerValidations, isUserLoggedIn, showLoginRequiredModal } from "../../../../utils/commonfunction";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const ITEMS_PER_PAGE = 12;

function LiveStreams() {
  const navigate = useNavigate();
  const [Streams, setStreams] = useState({ live: [], upcoming: [], past: [] });
  const [myStreams, setMyStreams] = useState(null);
  const [myStreamsLoading, setMyStreamsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "live";
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);
  const { t } = useTranslation();

  const reduxUserData = useSelector((state) => state.profile?.profileData);
  const localUser = getUser(ROLE.USER) || getUser(ROLE.ADVERTISER) || {};
  const userData = (reduxUserData && (reduxUserData._id || reduxUserData.id)) ? reduxUserData : localUser;
  const isAdvertiser = Boolean(
    (userData?.token || localUser?.token) && (
      userData?.isAdvertiser === true ||
      userData?.role === ROLE.ADVERTISER ||
      userData?.role === "advertiser" ||
      localUser?.isAdvertiser === true ||
      localUser?.role === ROLE.ADVERTISER ||
      localUser?.role === "advertiser"
    )
  );

  const staffCount = Number(localStorage.getItem("staff_invitations_count") || 0);
  const [hasStaffAccess, setHasStaffAccess] = useState(
    Boolean(
      userData?.isStaff === true ||
      userData?.role === "team_member" ||
      userData?.role === "staff" ||
      localUser?.isStaff === true ||
      localUser?.role === "team_member" ||
      localUser?.role === "staff" ||
      Boolean(userData?.seller_id || localUser?.seller_id) ||
      staffCount > 0
    )
  );

  useEffect(() => {
    if (userData?.token || localUser?.token) {
      getMyInvitations()
        .then((res) => {
          if (res && res.success && Array.isArray(res.data)) {
            const accepted = res.data.filter(inv => inv.status === 'Accepted' || (!inv.status && inv.seller_id));
            if (accepted.length > 0) {
              setHasStaffAccess(true);
            }
          }
        })
        .catch((err) => console.log("Error checking staff invitations:", err));
    }
  }, [userData?.token, localUser?.token]);

  const canAccessMyStreams = isAdvertiser || hasStaffAccess;
  const activeTab = (rawTab === "my_streams" && !canAccessMyStreams) ? "live" : rawTab;

  useEffect(() => {
    if (searchParams.get("tab") === "my_streams" && !canAccessMyStreams) {
      setSearchParams({ tab: "live" }, { replace: true });
    }
  }, [searchParams, canAccessMyStreams, setSearchParams]);

  const [globalData, setGlobalData] = useState({});
  const myGlobalData = useSelector((state) => state.globalData);
  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      const settingsData = JSON.parse(myGlobalData?.data);
      setGlobalData(settingsData);
    }
  }, [myGlobalData]);

  const fetchStreams = useCallback(async (search = "", pageNum = 1, append = false) => {
    if (activeTab === "my_streams") return;

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setIsFetchingMore(true);
    }

    try {
      const res = await GetStreams({
        search,
        tab: activeTab,
        page: pageNum,
        limit: ITEMS_PER_PAGE
      });

      if (res.success && res.data) {
        const liveItems = res.data.live || [];
        const upcomingItems = res.data.upcoming || [];
        const pastItems = res.data.past || [];

        const activeList = activeTab === "past" ? pastItems : activeTab === "upcoming" ? upcomingItems : liveItems;
        const currentCount = activeList.length;
        const serverHasMore = res.data.pagination?.hasMore;
        const computedHasMore = serverHasMore !== undefined ? serverHasMore : currentCount >= ITEMS_PER_PAGE;

        setHasMore(computedHasMore);

        if (append && pageNum > 1) {
          setStreams((prev) => {
            const mergeUnique = (existing = [], incoming = []) => {
              const existingIds = new Set(existing.map((item) => String(item._id)));
              const uniqueIncoming = incoming.filter((item) => !existingIds.has(String(item._id)));
              return [...existing, ...uniqueIncoming];
            };

            return {
              live: activeTab === "live" ? mergeUnique(prev?.live, liveItems) : (prev?.live || []),
              upcoming: activeTab === "upcoming" ? mergeUnique(prev?.upcoming, upcomingItems) : (prev?.upcoming || []),
              past: activeTab === "past" ? mergeUnique(prev?.past, pastItems) : (prev?.past || []),
            };
          });
        } else {
          setStreams({
            live: liveItems,
            upcoming: upcomingItems,
            past: pastItems,
          });
        }
      }
    } catch (err) {
      console.error("fetchStreams error:", err);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [activeTab]);

  const fetchMyStreams = useCallback(async (search = "") => {
    if (!canAccessMyStreams) {
      setMyStreams({ incoming: [] });
      return;
    }
    const token = userData?.token || localUser?.token;
    if (!token) {
      setMyStreams({ incoming: [] });
      return;
    }
    setMyStreamsLoading(true);
    try {
      let combinedIncoming = [];
      let combinedLive = [];

      if (isAdvertiser) {
        const res = await GetAdvertiserStreams({ search });
        if (res.success && res.data) {
          const inc = res.data.incoming || res.data.upcoming || [];
          const liv = res.data.live || [];
          combinedIncoming = [...inc];
          combinedLive = [...liv];
        }
      }

      if (hasStaffAccess) {
        const staffRes = await getStaffAssignedStreams({ search, tab: "all", page: 1, per_page: 50 });
        if (staffRes.success && staffRes.data) {
          const staffInc = staffRes.data.incoming || staffRes.data.upcoming || [];
          const staffLiv = staffRes.data.live || [];

          staffInc.forEach((s) => {
            if (!combinedIncoming.some((item) => String(item._id) === String(s._id))) {
              combinedIncoming.push({ ...s, fromStaff: true });
            }
          });
          staffLiv.forEach((s) => {
            if (!combinedLive.some((item) => String(item._id) === String(s._id))) {
              combinedLive.push({ ...s, fromStaff: true });
            }
          });
        }
      }

      setMyStreams({ incoming: combinedIncoming, live: combinedLive });
    } catch (err) {
      console.error("fetchMyStreams error:", err);
    } finally {
      setMyStreamsLoading(false);
    }
  }, [canAccessMyStreams, isAdvertiser, hasStaffAccess, userData?.token, localUser?.token]);

  // Initial and tab/search change load
  useEffect(() => {
    setPage(1);
    setHasMore(true);

    const delayDebounceFn = setTimeout(() => {
      if (activeTab === "my_streams") {
        fetchMyStreams(searchTerm);
      } else {
        fetchStreams(searchTerm, 1, false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, activeTab, fetchStreams, fetchMyStreams]);

  // Infinite scroll intersection observer
  useEffect(() => {
    if (activeTab === "my_streams") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !isFetchingMore) {
          setPage((prevPage) => {
            const nextPage = prevPage + 1;
            fetchStreams(searchTerm, nextPage, true);
            return nextPage;
          });
        }
      },
      { threshold: 0.1, rootMargin: "250px" }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasMore, loading, isFetchingMore, searchTerm, activeTab, fetchStreams]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleBroadcast = (broadcast) => {
      if (broadcast?.event === SOCKET_EVENTS.STREAM_STARTED || broadcast?.event === SOCKET_EVENTS.STREAM_ENDED) {
        if (activeTab === "my_streams") {
          fetchMyStreams(searchTerm);
        } else {
          fetchStreams(searchTerm, 1, false);
        }
      }
    };

    SOCKET_CLIENT.on(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);

    return () => {
      SOCKET_CLIENT.off(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);
    };
  }, [searchTerm, activeTab, fetchStreams, fetchMyStreams]);

  const handleDeleteStream = (item) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure", "Are you sure?"),
      text: t("you_not_be_able_to_revert_this", "You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FF63B5",
      cancelButtonColor: "#6B7280",
      confirmButtonText: t("btn_yes_delete", "Yes, delete it!"),
      cancelButtonText: t("btn_cancel", "Cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteStream(item._id)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || t("msg_stream_deleted", "Stream deleted successfully"),
                ...SWAL_SETTINGS,
              });
              fetchMyStreams(searchTerm);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.error("delete stream error:", error);
          });
      }
    });
  };

  const handleStartStream = async (item) => {
    try {
      const startResp = await startStream({ _id: item._id });
      if (!startResp.success) return;

      const stream = startResp.data.stream;

      await createAndForwardStream({
        zegoStreamId: stream._id,
      });

      navigate(
        `/advertiser/streams/live-streams/${stream.seller_id}/${stream.room_id}/${stream._id}`
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemindMe = async (item) => {
    const user = getUser(ROLE.USER);
    if (!user || !user.token) {
      Swal.fire({
        icon: "error",
        text: "Please login to perform this action",
      });
      return;
    }
    try {
      const resp = await subscribeReminder(item._id);
      if (resp.success) {
        Swal.fire({
          icon: 'success',
          text: `We will notify you when "${item.title}" starts!`,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        setStreams(prev => ({
          ...prev,
          upcoming: prev.upcoming.map(s => s._id === item._id ? { ...s, isReminderSet: true } : s)
        }));
      }
    } catch (error) {
      console.error("Failed to set reminder:", error);
    }
  };

  const handleJoinStream = (item) => {
    const isPast = activeTab === "past" || item.autoStatus === "past";
    const isUpcoming = activeTab === "upcoming" || new Date(item.date) > currentTime;
    navigate(`/user/staff-stream/${item._id}`, {
      state: {
        isPast,
        isUpcoming,
        permissions: userData?.permissions || []
      }
    });
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const handleViewStream = (item) => {
    const sellerId = item?.seller?._id || item?.seller_id || (typeof item?.seller === "string" ? item?.seller : "");
    const targetUrl = `/user/live-streams/${sellerId}/${item?.room_id}/${item?._id}`;
    if (!isUserLoggedIn()) {
      showLoginRequiredModal({
        navigate,
        redirectUrl: targetUrl,
        text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
      });
      return;
    }
    navigate(targetUrl);
  };

  const TABS = [
    ...(canAccessMyStreams ? [{ key: "my_streams", label: t("streams.my_streams", "My Streams") }] : []),
    { key: "live", label: t("streams.live", "Live") },
    { key: "upcoming", label: t("streams.upcoming", "Upcoming") },
    { key: "past", label: t("streams.past", "Past") },
  ];

  const isLoading = activeTab === "my_streams" ? myStreamsLoading : loading;
  const myUpcomingStreams = myStreams?.incoming || myStreams?.upcoming || [];

  return (
    <div className="home-bg min-h-screen flex flex-col">
      {String(globalData?.stream_tab_visibility) === "false" ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 md:p-20 text-center w-full max-w-4xl mx-auto my-10">
            <h2 className="heading-web-h2 text-[var(--black-scale-900)] mb-4">{t("streams.coming_soon", "Coming Soon")}</h2>
            <p className="text-zinc-500 text-lg">{t("streams.coming_soon_desc", "Live streams are coming to this space very soon. Stay tuned!")}</p>
          </div>
        </div>
      ) : (
        <>
          <InnerTitle
            title={t("streams.placeholder.search", "Search Streams")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="flex gap-2 mb-8 border-b border-gray-200 justify-center items-center overflow-x-auto no-scrollbar">
            {TABS?.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-6 py-3 text-base font-semibold whitespace-nowrap border-b-2 transition-all duration-300 ${activeTab === tab.key
                  ? "border-[var(--secondary-500)] text-[var(--secondary-500)]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {activeTab === "my_streams" && canAccessMyStreams && (
                <section className="past-streaming-section bg-white py-8 md:py-12 lg:py-16 !pt-0">
                  <div className="container">
                    {!userData?.token && !localUser?.token ? (
                      <div className="text-center py-10">
                        <p className="text-gray-500 text-lg font-medium mb-4">
                          {t("streams.login_to_view", "Please login to view your streams")}
                        </p>
                        <button
                          onClick={() => navigate("/user/login")}
                          className="btn btn-pri"
                        >
                          {t("common.login", "Login")}
                        </button>
                      </div>
                    ) : myUpcomingStreams && myUpcomingStreams.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {myUpcomingStreams.map((item, index) => {
                          const isStreamOwner = isAdvertiser && (!item.fromStaff || String(item.seller_id?._id || item.seller_id) === String(userData?._id || localUser?._id));

                          const primaryBtn = isStreamOwner ? t("streams.start", "Start") : t("streams.join_stream", "Join Stream");
                          const primaryDisabled = isStreamOwner ? (new Date(item.date) > currentTime) : false;
                          const primaryTitle = isStreamOwner && (new Date(item.date) > currentTime) ? t("streams.stream_not_started_yet", "Stream start time has not arrived yet") : "";
                          const onPrimaryClick = () => {
                            if (isStreamOwner) {
                              if (new Date(item.date) <= currentTime) {
                                handleStartStream(item);
                              }
                            } else {
                              handleJoinStream(item);
                            }
                          };

                          const secBtn = isStreamOwner ? t("streams.join_stream", "Join Stream") : null;
                          const onSecClick = isStreamOwner ? () => handleJoinStream(item) : null;

                          return (
                            <div key={item._id || index} className="cursor-pointer">
                              <Card
                                images={item.thumbnail}
                                title={item.title}
                                subtitle={item.description || t("streams.duration_minutes", { duration: item.duration }, `Duration: ${item.duration} minutes`)}
                                time={item.duration}
                                dateTime={formatDateAndTime(item.date)}
                                button={primaryBtn}
                                buttonDisabled={primaryDisabled}
                                buttonTitle={primaryTitle}
                                onButtonClick={onPrimaryClick}
                                secondaryButton={secBtn}
                                onSecondaryButtonClick={onSecClick}
                                editDelete={isStreamOwner}
                                onDelete={() => handleDeleteStream(item)}
                                onClick={() => navigate(`/user/streams/stream-details/${item?._id}`)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-gray-500 text-lg font-medium">
                          {t("streams.no_upcoming", "No Upcoming Streams Available")}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {activeTab === "live" && (
                <section className="past-streaming-section bg-white py-8 md:py-12 lg:py-16 !pt-0">
                  <div className="container">
                    {Streams?.live && Streams.live.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {Streams.live.map((item, index) => {
                            const isStreamOwner = isAdvertiser && String(item.seller_id?._id || item.seller_id || item.seller?._id) === String(userData?._id || localUser?._id);

                            let primaryBtn = "Watch Now";
                            let onPrimaryClick = () => handleViewStream(item);
                            let secBtn = null;
                            let onSecClick = null;

                            if (isStreamOwner) {
                              primaryBtn = t("streams.start_stream", "Start Stream");
                              onPrimaryClick = () => handleStartStream(item);
                              secBtn = t("streams.join_stream", "Join Stream");
                              onSecClick = () => handleJoinStream(item);
                            } else if (hasStaffAccess) {
                              secBtn = t("streams.join_stream", "Join Stream");
                              onSecClick = () => handleJoinStream(item);
                            }

                            return (
                              <div key={item._id || index}>
                                <Card
                                  images={item.thumbnail}
                                  title={item.title}
                                  subtitle={item.description}
                                  time={item.time}
                                  live={item.isLive}
                                  date={formatDateAndTime(item.date)}
                                  views={item.views}
                                  category={item.category}
                                  button={primaryBtn}
                                  onButtonClick={onPrimaryClick}
                                  secondaryButton={secBtn}
                                  onSecondaryButtonClick={onSecClick}
                                  onClick={() => handleViewStream(item)}
                                />
                              </div>
                            );
                          })}
                        </div>

                        {isFetchingMore && (
                          <div className="flex justify-center items-center py-8">
                            <div className="w-8 h-8 border-3 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        <div ref={observerTarget} className="h-6 w-full" />
                      </>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-gray-500 text-lg font-medium">
                          {t("streams.no_live", "No Live Streams Available")}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {activeTab === "upcoming" && (
                <section className="past-streaming-section bg-white py-8 md:py-12 lg:py-16 !pt-0">
                  <div className="container">
                    {Streams?.upcoming && Streams?.upcoming.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {Streams?.upcoming?.map((item, index) => (
                            <div key={item._id || index}>
                              <Card
                                onClick={() => {
                                  const targetUrl = `/user/streams/stream-details/${item?._id}`;
                                  if (!isUserLoggedIn()) {
                                    showLoginRequiredModal({
                                      navigate,
                                      redirectUrl: targetUrl,
                                      text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
                                    });
                                    return;
                                  }
                                  navigate(targetUrl);
                                }}
                                images={item.thumbnail}
                                title={item.title}
                                subtitle={item.description}
                                time={item?.duration}
                                date={formatDateAndTime(item.date)}
                                live={item.live}
                                buttonReminder={new Date(item.date) > currentTime ? (item?.isReminderSet ? "Reminded" : "Remind Me") : ""}
                                onReminderClick={() => {
                                  if (!isUserLoggedIn()) {
                                    showLoginRequiredModal({
                                      navigate,
                                      redirectUrl: window.location.pathname + window.location.search,
                                      text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
                                    });
                                    return;
                                  }
                                  if (!item.isReminderSet && new Date(item.date) > currentTime) handleRemindMe(item);
                                }}
                              />
                            </div>
                          ))}
                        </div>

                        {isFetchingMore && (
                          <div className="flex justify-center items-center py-8">
                            <div className="w-8 h-8 border-3 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        <div ref={observerTarget} className="h-6 w-full" />
                      </>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-gray-500 text-lg font-medium">
                          {t("streams.no_upcoming", "No Upcoming Streams Available")}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {activeTab === "past" && (
                <section className="past-streaming-section bg-white py-8 md:py-12 lg:py-16 !pt-0">
                  <div className="container">
                    {Streams?.past && Streams?.past.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {Streams?.past?.map((item, index) => (
                            <div key={item._id || index}>
                              <Card
                                onClick={() => {
                                  const targetUrl = `/user/streams/stream-details/${item?._id}`;
                                  if (!isUserLoggedIn()) {
                                    showLoginRequiredModal({
                                      navigate,
                                      redirectUrl: targetUrl,
                                      text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
                                    });
                                    return;
                                  }
                                  navigate(targetUrl);
                                }}
                                images={item.thumbnail}
                                title={item.title}
                                subtitle={item.description}
                                time={item.duration}
                                live={item.live}
                                date={formatDateAndTime(item.date)}
                                button="Rewatch"
                                buttonDisabled={!item?.recording_url}
                                onButtonClick={() => handleViewStream(item)}
                              />
                            </div>
                          ))}
                        </div>

                        {isFetchingMore && (
                          <div className="flex justify-center items-center py-8">
                            <div className="w-8 h-8 border-3 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        <div ref={observerTarget} className="h-6 w-full" />
                      </>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-gray-500 text-lg font-medium">
                          {t("streams.no_past_streams", "No Past Streams Available")}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default LiveStreams;
